import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';

interface DeleteRecordTool {
  name: AgentTools.sf_delete_record;
  args: {
    objectName: string;
    recordId: string;
  };
}

export type DeleteRecordToolCall = ToolCall<DeleteRecordTool['name'], DeleteRecordTool['args']>;

export type DeleteRecordToolCallSummary = ToolCallSummary<
  DeleteRecordTool['name'],
  DeleteRecordTool['args']
>;

interface DeleteRecordResult {
  id: string;
  success: boolean;
  errors: string[];
}

export function DeleteRecordToolCall({ toolCall }: { toolCall: DeleteRecordToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<DeleteRecordResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_delete_record.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.sf_delete_record.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.sf_delete_record.title.success');
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallDataList
              items={[
                {
                  label: t('toolCalls.sf_delete_record.results.deletedRecordId'),
                  value: parsedResult.results.id,
                },
              ]}
            />
          )
        ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.sf_delete_record.parameters.objectName'),
              value: toolCall.args.objectName,
            },
            {
              label: t('toolCalls.sf_delete_record.parameters.recordId'),
              value: toolCall.args.recordId,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
